import { FC, useEffect, useMemo, useRef } from 'react';
import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDebounce, useEffectDeepCompare, useScript } from '@netiva/classifieds-common';
import { Loader } from '@netiva/classifieds-ui';

import { CarouselConfiguration, ListDetailConfiguration } from './components';
import { usePlatforms } from './usePlatforms';
import { getScriptCode } from './utils';
import { useAppDispatch, useAppSelector } from '@/store';
import { sdkActions, SdkParams } from '@/store/sdk';
import { useMatomoTagManager } from '@/hooks';

export const SdkPage: FC = () => {
  const { t } = useTranslation();

  const { trackMatomoEvent } = useMatomoTagManager();

  const dispatch = useAppDispatch();
  const { params, scriptVersion } = useAppSelector((state) => state.sdk);

  // Reload script on params change
  const debouncedParams = useDebounce(params, 100);
  useEffectDeepCompare(() => {
    dispatch(sdkActions.reloadScript());
  }, [dispatch, debouncedParams]);

  const { platforms, isLoading } = usePlatforms();
  const currentPlatform = useMemo(
    () => platforms.find((p) => p.key === params.platform) || platforms[0],
    [params.platform, platforms]
  );

  const form = useForm<SdkParams>({ values: params, mode: 'all' });

  // Update store on form changes & apply default values
  const formValues = useWatch({ control: form.control });
  const { accountId } = useAppSelector((state) => state.account);
  useEffectDeepCompare(() => {
    dispatch(
      sdkActions.setParams({
        ...formValues,
        language: formValues.language || 'de',
        accountId: formValues.accountId || accountId?.toString(),
        platform: formValues.platform || currentPlatform?.key,
      })
    );
  }, [accountId, currentPlatform?.key, dispatch, formValues]);

  const loaderScriptUrl = useMemo(() => {
    if (!currentPlatform) {
      return '';
    }
    const baseUrl = currentPlatform.publicBaseUrl;
    //const baseUrl = 'http://localhost:3000';
    return `${baseUrl}/scripts/sdk/loader-v1.js`;
  }, [currentPlatform]);

  const loaderScriptCode = useMemo(() => getScriptCode('nc-sdk-loader', loaderScriptUrl), [loaderScriptUrl]);

  useScript('nc-sdk-loader', loaderScriptUrl ? `${loaderScriptUrl}?refresh=${scriptVersion}` : undefined);

  // matomo tracking
  const hasTrackedPageLoadRef = useRef(false);

  useEffect(() => {
    if (hasTrackedPageLoadRef.current) return;
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-Products',
      trackingAction: 'View_SDK-Integration',
    });
    hasTrackedPageLoadRef.current = true;
  }, [trackMatomoEvent]);

  return (
    <Box position="relative">
      <Loader isLoading={isLoading} showOverlay />

      <FormProvider {...form}>
        <Tabs>
          <TabList>
            <Tab>{t('sdk.listDetail.title')}</Tab>
            <Tab>{t('sdk.carousel.title')}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <ListDetailConfiguration loaderScriptCode={loaderScriptCode} />
            </TabPanel>
            <TabPanel>
              <CarouselConfiguration loaderScriptCode={loaderScriptCode} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </FormProvider>
    </Box>
  );
};
